/* Admin Matches page — HLTV-style list view (toggle next to the filters; Matches.js keeps the cards).
 * Live matches on top, then one block per day (newest first). One compact row per match:
 * time | team 1 + logo | score | logo + team 2 | map / BO | server | actions. The whole row opens the
 * match (native <Link>, survives the 5 s list poll); the ⋯ menu has the same actions as the cards. */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../../utils/timeUtils';
import { teamColor } from '../teams/TeamLogo';
import './matchListHltv.css';

// Logo only when the team has an uploaded one (team1/2_avatar_v sent with each match — no request for
// teams without a logo; ?v= makes it cacheable), else the coloured initial like the Teams page.
// Also used by the card view (Matches.js) with className="mcard-logo".
export function Logo({ id, name, avatarV, className = 'hl-logo' }) {
  const [broken, setBroken] = React.useState(false);
  if (id && avatarV && !broken) {
    return <span className={className}><img src={`/api/teams/${id}/avatar?v=${avatarV}`} alt="" loading="lazy" onError={() => setBroken(true)} /></span>;
  }
  const c = teamColor(name);
  return <span className={`${className} hl-logo-initial`} style={{ background: `${c}22`, borderColor: `${c}55`, color: c }} aria-hidden="true">{String(name || '?').trim().charAt(0).toUpperCase()}</span>;
}

/**
 * The one action icon on a match (card + HLTV row). Opening the match is the click on the card/row
 * itself, so there's no menu: finished / cancelled → Delete (trash), pending → Cancel (×), live → none.
 * Both still go through the page's confirm dialog.
 */
export function MatchActionIcon({ match, onCancel, onDelete, className = 'hl-icon' }) {
  const { t } = useTranslation();
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(match.id); };
  if (match.status === 'pending') {
    return (
      <button type="button" className={`${className} amber`} onClick={stop(onCancel)} title={t('matches.cancelMatch', 'Cancel match')} aria-label={t('matches.cancelMatch', 'Cancel match')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
      </button>
    );
  }
  if (match.status === 'live') return null;
  return (
    <button type="button" className={`${className} red`} onClick={stop(onDelete)} title={t('matches.deleteMatch', 'Delete match')} aria-label={t('matches.deleteMatch', 'Delete match')}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></svg>
    </button>
  );
}

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export default function MatchListHltv({ matches, canManage, onCancel, onDelete }) {
  const { t, i18n } = useTranslation();
  const when = (m) => parseServerDate(m.started_at || m.created_at);

  const live = matches.filter(m => m.status === 'live');
  const rest = matches.filter(m => m.status !== 'live');
  const groups = [];
  const byKey = new Map();
  for (const m of rest) {
    const d = when(m);
    const k = d ? dayKey(d) : 'none';
    if (!byKey.has(k)) { const g = { key: k, date: d, items: [] }; byKey.set(k, g); groups.push(g); }
    byKey.get(k).items.push(m);
  }
  const today = new Date(), yesterday = new Date(Date.now() - 86400000);
  const dayTitle = (d) => {
    if (!d) return t('matches.hlNoDate', 'No date');
    if (dayKey(d) === dayKey(today)) return t('matches.hlToday', 'Today');
    if (dayKey(d) === dayKey(yesterday)) return t('matches.hlYesterday', 'Yesterday');
    return d.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const row = (m) => {
    const isLive = m.status === 'live', isDone = m.status === 'finished', isCancelled = m.status === 'cancelled';
    const bo = parseInt(m.best_of, 10) || 1;
    const s1 = bo > 1 ? (m.map_wins_team1 ?? 0) : (m.score_team1 ?? 0);
    const s2 = bo > 1 ? (m.map_wins_team2 ?? 0) : (m.score_team2 ?? 0);
    const win1 = isDone && s1 > s2, win2 = isDone && s2 > s1;
    const d = when(m);
    const mapName = m.map ? m.map.replace(/^(de_|cs_)/, '') : 'TBD';
    return (
      <div key={m.id} className={`hl-row${isLive ? ' live' : ''}${isCancelled ? ' cancelled' : ''}`}>
        <Link to={`/admin/matches/${m.id}`} className="hl-link" aria-label={`Open match ${m.id}`} />
        <div className="hl-time">
          {isLive ? <span className="hl-live">{t('matchStatus.live', 'Live')}</span>
            : isCancelled ? <span className="hl-tag red">{t('matchStatus.cancelled', 'Cancelled')}</span>
              : m.status === 'pending' ? <span className="hl-tag amber">{d ? d.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : t('matchStatus.pending', 'Pending')}</span>
                : <span>{d ? d.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : '–'}</span>}
        </div>
        <div className={`hl-team t1${win1 ? ' win' : ''}${win2 ? ' lose' : ''}`}>
          <span className="hl-name">{m.team1_name}</span>
          <Logo id={m.team1_id} avatarV={m.team1_avatar_v} name={m.team1_name} />
        </div>
        <div className={`hl-score${isLive ? ' live' : ''}`}>
          {m.status === 'pending' ? <span className="hl-vs">vs</span> : (
            <><b className={win1 ? 'w' : win2 ? 'l' : ''}>{s1}</b><i>-</i><b className={win2 ? 'w' : win1 ? 'l' : ''}>{s2}</b></>
          )}
          {isLive && bo > 1 && <span className="hl-mapscore">{m.score_team1 ?? 0}-{m.score_team2 ?? 0}</span>}
        </div>
        <div className={`hl-team t2${win2 ? ' win' : ''}${win1 ? ' lose' : ''}`}>
          <Logo id={m.team2_id} avatarV={m.team2_avatar_v} name={m.team2_name} />
          <span className="hl-name">{m.team2_name}</span>
        </div>
        <div className="hl-meta">
          <span className="hl-format">{bo > 1 ? `BO${bo}` : mapName}</span>
          {bo > 1 && isLive && <span className="hl-sub">{mapName}</span>}
        </div>
        <div className="hl-server" title={m.server_city || m.server_country_name || ''}>
          {m.server_country_code && <img src={`https://flagcdn.com/16x12/${m.server_country_code.toLowerCase()}.png`} alt="" width="16" height="12" onError={e => { e.target.style.display = 'none'; }} />}
          <span>{m.server_name || '—'}</span>
          <span className="hl-id">#{m.id}</span>
        </div>
        <div className="hl-actions">
          {canManage(m) && <MatchActionIcon match={m} onCancel={onCancel} onDelete={onDelete} />}
        </div>
      </div>
    );
  };

  if (!matches.length) return <div className="hl-empty">{t('matches.noMatchesFound', 'No matches found')}</div>;

  return (
    <div className="hl-list">
      {live.length > 0 && (
        <section className="hl-group">
          <div className="hl-head live">{t('matches.hlLiveMatches', 'Live matches')}<span className="hl-count">{live.length}</span></div>
          {live.map(row)}
        </section>
      )}
      {groups.map(g => (
        <section key={g.key} className="hl-group">
          <div className="hl-head">{dayTitle(g.date)}<span className="hl-count">{g.items.length}</span></div>
          {g.items.map(row)}
        </section>
      ))}
    </div>
  );
}
